import getClientConfig from './getClientConfig';
import Cookies from 'universal-cookie';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const ID_TOKEN_KEY = 'id_token';

// Helper function to determine storage method based on environment variable
const shouldUseLocalStorage = () => {
    return getClientConfig().enableLocalStorageForTokens;
};

// Cookie-based storage functions
const getAccessTokenFromCookie = () => {
    const cookies = new Cookies();
    return cookies.get(ACCESS_TOKEN_KEY);
};

const setAccessTokenToCookie = (token) => {
    const cookies = new Cookies();
    cookies.set(ACCESS_TOKEN_KEY, token, {
        maxAge: 3600,
        secure: true,
        sameSite: 'strict',
        path: '/'
    });
};

const removeAccessTokenFromCookie = () => {
    const cookies = new Cookies();
    cookies.remove(ACCESS_TOKEN_KEY, { path: '/' });
};

const getRefreshTokenFromCookie = () => {
    const cookies = new Cookies();
    return cookies.get(REFRESH_TOKEN_KEY);
};

const setRefreshTokenToCookie = (token) => {
    const cookies = new Cookies();
    cookies.set(REFRESH_TOKEN_KEY, token, {
        maxAge: 86400,
        secure: true,
        sameSite: 'strict',
        path: '/'
    });
};

const removeRefreshTokenFromCookie = () => {
    const cookies = new Cookies();
    cookies.remove(REFRESH_TOKEN_KEY, { path: '/' });
};

const getIdTokenFromCookie = () => {
    const cookies = new Cookies();
    return cookies.get(ID_TOKEN_KEY);
};

const setIdTokenToCookie = (token) => {
    const cookies = new Cookies();
    cookies.set(ID_TOKEN_KEY, token, {
        maxAge: 3600,
        secure: true,
        sameSite: 'strict',
        path: '/'
    });
};

const removeIdTokenFromCookie = () => {
    const cookies = new Cookies();
    cookies.remove(ID_TOKEN_KEY, { path: '/' });
};

// localStorage-based storage functions
const getAccessTokenFromLocalStorage = () => localStorage.getItem(ACCESS_TOKEN_KEY);
const setAccessTokenToLocalStorage = (token) => localStorage.setItem(ACCESS_TOKEN_KEY, token);
const removeAccessTokenFromLocalStorage = () => localStorage.removeItem(ACCESS_TOKEN_KEY);

const getRefreshTokenFromLocalStorage = () => localStorage.getItem(REFRESH_TOKEN_KEY);
const setRefreshTokenToLocalStorage = (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token);
const removeRefreshTokenFromLocalStorage = () => localStorage.removeItem(REFRESH_TOKEN_KEY);

const getIdTokenFromLocalStorage = () => localStorage.getItem(ID_TOKEN_KEY);
const setIdTokenToLocalStorage = (token) => localStorage.setItem(ID_TOKEN_KEY, token);
const removeIdTokenFromLocalStorage = () => localStorage.removeItem(ID_TOKEN_KEY);

// Public API - automatically chooses storage method based on feature flag
export const getAccessToken = () => {
    return shouldUseLocalStorage()
        ? getAccessTokenFromLocalStorage()
        : getAccessTokenFromCookie();
};

export const setAccessToken = (token) => {
    if (shouldUseLocalStorage()) {
        setAccessTokenToLocalStorage(token);
    } else {
        setAccessTokenToCookie(token);
    }
};

export const removeAccessToken = () => {
    if (shouldUseLocalStorage()) {
        removeAccessTokenFromLocalStorage();
    } else {
        removeAccessTokenFromCookie();
    }
};

export const getRefreshToken = () => {
    return shouldUseLocalStorage()
        ? getRefreshTokenFromLocalStorage()
        : getRefreshTokenFromCookie();
};

export const setRefreshToken = (token) => {
    if (shouldUseLocalStorage()) {
        setRefreshTokenToLocalStorage(token);
    } else {
        setRefreshTokenToCookie(token);
    }
};

export const removeRefreshToken = () => {
    if (shouldUseLocalStorage()) {
        removeRefreshTokenFromLocalStorage();
    } else {
        removeRefreshTokenFromCookie();
    }
};

export const getIdToken = () => {
    return shouldUseLocalStorage()
        ? getIdTokenFromLocalStorage()
        : getIdTokenFromCookie();
};

export const setIdToken = (token) => {
    if (shouldUseLocalStorage()) {
        setIdTokenToLocalStorage(token);
    } else {
        setIdTokenToCookie(token);
    }
};

export const removeIdToken = () => {
    if (shouldUseLocalStorage()) {
        removeIdTokenFromLocalStorage();
    } else {
        removeIdTokenFromCookie();
    }
};

// Clear all tokens from both storage methods
export const clearAllTokens = () => {
    // Clear from localStorage
    removeAccessTokenFromLocalStorage();
    removeRefreshTokenFromLocalStorage();
    removeIdTokenFromLocalStorage();

    // Clear from cookies
    removeAccessTokenFromCookie();
    removeRefreshTokenFromCookie();
    removeIdTokenFromCookie();
};
